(() => {
  'use strict';

  const qs = (selector, root = document) => root.querySelector(selector);
  const qsa = (selector, root = document) => Array.from(root.querySelectorAll(selector));
  const lockBody = (locked) => document.body.classList.toggle('rt-locked', locked);
  const setHidden = (element, hidden) => { if (element) element.hidden = hidden; };

  function initReveal(root = document) {
    const items = qsa('[data-rt-reveal]:not([data-rt-reveal-ready])', root);
    if (!items.length) return;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced || !('IntersectionObserver' in window)) {
      items.forEach((item) => { item.dataset.rtRevealReady = 'true'; item.classList.add('is-visible'); });
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -40px' });
    items.forEach((item) => { item.dataset.rtRevealReady = 'true'; observer.observe(item); });
  }

  function initAnnouncement(root = document) {
    qsa('[data-announcement]:not([data-rt-ready])', root).forEach((bar) => {
      bar.dataset.rtReady = 'true';
      const items = qsa('[data-announcement-item]', bar);
      if (items.length < 2) return;
      let index = 0;
      const interval = Number(bar.dataset.interval || 5000);
      window.setInterval(() => {
        items[index].classList.remove('is-active');
        index = (index + 1) % items.length;
        items[index].classList.add('is-active');
      }, interval);
    });
  }

  function initStickyHeader(root = document) {
    const header = qs('[data-rt-header]', root) || qs('[data-rt-header]');
    if (!header || header.dataset.rtStickyReady) return;
    header.dataset.rtStickyReady = 'true';
    const bar = qs('[data-rt-sticky-bar]', header);
    if (!bar || !document.body.classList.contains('recore-sticky-header')) return;
    let threshold = bar.getBoundingClientRect().top + window.scrollY;
    const update = () => {
      const sticky = window.scrollY > threshold;
      bar.classList.toggle('is-sticky', sticky);
      header.style.paddingBottom = sticky ? `${bar.offsetHeight}px` : '';
    };
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', () => {
      bar.classList.remove('is-sticky');
      header.style.paddingBottom = '';
      threshold = bar.getBoundingClientRect().top + window.scrollY;
      update();
    }, { passive: true });
    update();
  }

  function initSidebar(root = document) {
    const sidebar = qs('[data-rt-sidebar]', root) || qs('[data-rt-sidebar]');
    if (!sidebar || sidebar.dataset.rtReady) return;
    sidebar.dataset.rtReady = 'true';
    const overlay = qs('[data-rt-drawer-overlay]');
    const openers = qsa('[data-rt-sidebar-open]');
    const closeButton = qs('[data-rt-sidebar-close]', sidebar);
    let lastFocused = null;
    const close = () => {
      sidebar.classList.remove('is-open');
      sidebar.setAttribute('aria-hidden', 'true');
      openers.forEach((button) => button.setAttribute('aria-expanded', 'false'));
      overlay?.classList.remove('is-visible');
      window.setTimeout(() => setHidden(overlay, true), 280);
      lockBody(false);
      lastFocused?.focus();
    };
    const open = (event) => {
      lastFocused = event?.currentTarget || document.activeElement;
      setHidden(overlay, false);
      requestAnimationFrame(() => overlay?.classList.add('is-visible'));
      sidebar.classList.add('is-open');
      sidebar.setAttribute('aria-hidden', 'false');
      openers.forEach((button) => button.setAttribute('aria-expanded', 'true'));
      lockBody(true);
      closeButton?.focus();
    };
    openers.forEach((button) => button.addEventListener('click', open));
    closeButton?.addEventListener('click', close);
    overlay?.addEventListener('click', close);
    document.addEventListener('keydown', (event) => { if (event.key === 'Escape' && sidebar.classList.contains('is-open')) close(); });
  }

  function initSearch(root = document) {
    const modal = qs('[data-rt-search-modal]', root) || qs('[data-rt-search-modal]');
    if (!modal || modal.dataset.rtReady) return;
    modal.dataset.rtReady = 'true';
    const openers = qsa('[data-rt-search-open]');
    const closers = qsa('[data-rt-search-close]', modal);
    const input = qs('[data-rt-search-input]', modal);
    let lastFocused = null;
    const close = () => {
      modal.setAttribute('aria-hidden', 'true');
      openers.forEach((button) => button.setAttribute('aria-expanded', 'false'));
      lockBody(false);
      setHidden(modal, true);
      lastFocused?.focus();
    };
    const open = (event) => {
      lastFocused = event?.currentTarget || document.activeElement;
      setHidden(modal, false);
      modal.setAttribute('aria-hidden', 'false');
      openers.forEach((button) => button.setAttribute('aria-expanded', 'true'));
      lockBody(true);
      window.setTimeout(() => input?.focus(), 50);
    };
    openers.forEach((button) => button.addEventListener('click', open));
    closers.forEach((button) => button.addEventListener('click', close));
    document.addEventListener('keydown', (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') { event.preventDefault(); open(event); }
      if (event.key === 'Escape' && !modal.hidden) close();
    });
  }

  function initFilters(root = document) {
    const filters = qs('[data-rt-filters]', root) || qs('[data-rt-filters]');
    if (!filters || filters.dataset.rtReady) return;
    filters.dataset.rtReady = 'true';
    const overlay = qs('[data-rt-filters-overlay]');
    const openButton = qs('[data-rt-filters-open]');
    const closeButton = qs('[data-rt-filters-close]', filters);
    const close = () => {
      filters.classList.remove('is-open');
      filters.setAttribute('aria-hidden', window.innerWidth < 990 ? 'true' : 'false');
      openButton?.setAttribute('aria-expanded', 'false');
      overlay?.classList.remove('is-visible');
      window.setTimeout(() => setHidden(overlay, true), 280);
      lockBody(false);
    };
    const open = () => {
      setHidden(overlay, false);
      requestAnimationFrame(() => overlay?.classList.add('is-visible'));
      filters.classList.add('is-open');
      filters.setAttribute('aria-hidden', 'false');
      openButton?.setAttribute('aria-expanded', 'true');
      lockBody(true);
      closeButton?.focus();
    };
    openButton?.addEventListener('click', open);
    closeButton?.addEventListener('click', close);
    overlay?.addEventListener('click', close);
    document.addEventListener('keydown', (event) => { if (event.key === 'Escape' && filters.classList.contains('is-open')) close(); });

    qsa('[data-rt-filter-more]', filters).forEach((button) => {
      button.addEventListener('click', () => {
        const values = button.previousElementSibling;
        const expanded = !values.classList.contains('is-collapsed');
        values.classList.toggle('is-collapsed', expanded);
        button.textContent = expanded ? 'Show more' : 'Show less';
      });
    });
    qsa('[data-rt-filter-search]', filters).forEach((input) => {
      input.addEventListener('input', () => {
        const query = input.value.trim().toLowerCase();
        const group = input.closest('.rt-filter-group');
        qsa('[data-rt-filter-value]', group).forEach((option) => {
          option.hidden = query && !option.dataset.rtFilterValue.toLowerCase().includes(query);
        });
      });
    });
  }

  function initSort(root = document) {
    qsa('[data-rt-sort-select]:not([data-rt-ready])', root).forEach((select) => {
      select.dataset.rtReady = 'true';
      select.addEventListener('change', () => select.form?.submit());
    });
  }

  function initGridView(root = document) {
    const grid = qs('[data-rt-product-grid]', root) || qs('[data-rt-product-grid]');
    if (!grid || grid.dataset.rtReady) return;
    grid.dataset.rtReady = 'true';
    const buttons = qsa('[data-rt-grid-view]');
    const setView = (view) => {
      grid.classList.toggle('is-list', view === 'list');
      buttons.forEach((button) => button.classList.toggle('is-active', button.dataset.rtGridView === view));
      try { localStorage.setItem('recore-grid-view', view); } catch (_) {}
    };
    let saved = 'grid';
    try { saved = localStorage.getItem('recore-grid-view') || 'grid'; } catch (_) {}
    if (window.innerWidth < 750) saved = 'grid';
    setView(saved);
    buttons.forEach((button) => button.addEventListener('click', () => setView(button.dataset.rtGridView)));
  }

  function initBrandSearch(root = document) {
    const input = qs('[data-rt-brand-search]', root);
    const directory = qs('[data-rt-brand-directory]', root);
    if (!input || !directory || input.dataset.rtReady) return;
    input.dataset.rtReady = 'true';
    input.addEventListener('input', () => {
      const query = input.value.trim().toLowerCase();
      qsa('[data-rt-brand-name]', directory).forEach((card) => { card.hidden = query && !card.dataset.rtBrandName.includes(query); });
    });
  }

  function initGallery(root = document) {
    qsa('[data-product-gallery]:not([data-rt-ready])', root).forEach((gallery) => {
      gallery.dataset.rtReady = 'true';
      const main = qs('[data-gallery-main-image]', gallery) || qs('.recore-product-gallery__main img', gallery);
      if (!main) return;

      qsa('[data-gallery-thumb]', gallery).forEach((thumb) => {
        thumb.addEventListener('click', () => {
          const nextSrc = thumb.dataset.mediaSrc;
          if (!nextSrc) return;

          // Shopify image_tag outputs srcset. If srcset is left in place the browser can
          // keep displaying the previous responsive source even after main.src changes.
          // Clear it before swapping the selected gallery image.
          main.classList.add('is-switching');
          const image = new Image();
          image.onload = () => {
            main.removeAttribute('srcset');
            main.removeAttribute('sizes');
            main.src = nextSrc;
            main.alt = thumb.dataset.mediaAlt || '';
            main.removeAttribute('width');
            main.removeAttribute('height');
            window.requestAnimationFrame(() => main.classList.remove('is-switching'));
          };
          image.onerror = () => main.classList.remove('is-switching');
          image.src = nextSrc;

          qsa('[data-gallery-thumb]', gallery).forEach((item) => {
            const active = item === thumb;
            item.classList.toggle('is-active', active);
            item.setAttribute('aria-pressed', active ? 'true' : 'false');
          });

          thumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        });
      });
    });
  }

  function initQuantity(root = document) {
    qsa('[data-quantity]:not([data-rt-ready])', root).forEach((quantity) => {
      quantity.dataset.rtReady = 'true';
      const input = qs('input', quantity);
      qs('[data-quantity-minus]', quantity)?.addEventListener('click', () => { input.value = Math.max(Number(input.min || 1), Number(input.value || 1) - 1); });
      qs('[data-quantity-plus]', quantity)?.addEventListener('click', () => { input.value = Number(input.value || 1) + 1; });
    });
  }

  let toastTimer;
  function showToast(message) {
    const toast = qs('[data-recore-toast]');
    if (!toast) return;
    toast.textContent = message;
    toast.classList.add('is-visible');
    window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(() => toast.classList.remove('is-visible'), 3000);
  }

  async function updateCartCount() {
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) return;
    const cart = await response.json();
    qsa('[data-rt-cart-count], [data-cart-count]').forEach((bubble) => {
      bubble.textContent = cart.item_count;
      bubble.classList.toggle('is-empty', cart.item_count === 0);
    });
  }

  function initAjaxForms(root = document) {
    qsa('[data-rt-quick-add]:not([data-rt-ready]), .recore-product-form-js:not([data-rt-ready])', root).forEach((form) => {
      form.dataset.rtReady = 'true';
      form.addEventListener('submit', async (event) => {
        if (!window.fetch) return;
        event.preventDefault();
        const button = qs('[type="submit"]', form);
        const message = qs('[data-product-message]', form);
        const original = button?.textContent;
        try {
          if (button) { button.disabled = true; button.textContent = 'Adding…'; }
          const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form) });
          const result = await response.json();
          if (!response.ok) throw new Error(result.description || 'Unable to add this item.');
          await updateCartCount();
          if (message) message.textContent = 'Added to your cart.';
          showToast('Added to your cart');
        } catch (error) {
          if (message) message.textContent = error.message;
          showToast(error.message || 'Something went wrong.');
        } finally {
          if (button) { button.disabled = false; button.textContent = original; }
        }
      });
    });
  }

  function initSupport(root = document) {
    const support = qs('[data-rt-support]', root) || qs('[data-rt-support]');
    if (!support || support.dataset.rtReady) return;
    support.dataset.rtReady = 'true';
    const toggle = qs('[data-rt-support-toggle]', support);
    const panel = qs('[data-rt-support-panel]', support);
    const close = qs('[data-rt-support-close]', support);
    const setOpen = (open) => { panel.hidden = !open; toggle?.setAttribute('aria-expanded', String(open)); };
    toggle?.addEventListener('click', () => setOpen(panel.hidden));
    close?.addEventListener('click', () => setOpen(false));
    document.addEventListener('keydown', (event) => { if (event.key === 'Escape' && !panel.hidden) setOpen(false); });
  }


  function initSliders(root = document) {
    qsa('[data-rt-slider]:not([data-rt-slider-ready])', root).forEach((slider) => {
      const viewport = qs('[data-rt-slider-viewport]', slider);
      const track = qs('[data-rt-slider-track]', slider);
      const previous = qs('[data-rt-slider-prev]', slider);
      const next = qs('[data-rt-slider-next]', slider);
      const progress = qs('[data-rt-slider-progress]', slider);
      if (!viewport || !track) return;

      slider.dataset.rtSliderReady = 'true';

      const slides = () => Array.from(track.children).filter((item) => !item.hidden);
      const step = () => {
        const first = slides()[0];
        if (!first) return viewport.clientWidth;
        const styles = window.getComputedStyle(track);
        const gap = Number.parseFloat(styles.columnGap || styles.gap || '0') || 0;
        return first.getBoundingClientRect().width + gap;
      };

      const update = () => {
        const max = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
        const current = Math.min(max, Math.max(0, viewport.scrollLeft));
        if (previous) previous.disabled = current <= 3;
        if (next) next.disabled = current >= max - 3;
        if (progress) {
          const ratio = max > 0 ? current / max : 0;
          const visibleRatio = Math.min(1, viewport.clientWidth / Math.max(viewport.scrollWidth, 1));
          progress.style.width = `${Math.max(18, visibleRatio * 100)}%`;
          progress.style.transform = `translateX(${ratio * ((100 / Math.max(visibleRatio, .18)) - 100)}%)`;
        }
      };

      previous?.addEventListener('click', () => viewport.scrollBy({ left: -step(), behavior: 'smooth' }));
      next?.addEventListener('click', () => viewport.scrollBy({ left: step(), behavior: 'smooth' }));
      viewport.addEventListener('scroll', update, { passive: true });
      viewport.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight') viewport.scrollBy({ left: step(), behavior: 'smooth' });
        if (event.key === 'ArrowLeft') viewport.scrollBy({ left: -step(), behavior: 'smooth' });
      });
      if ('ResizeObserver' in window) new ResizeObserver(update).observe(viewport);
      else window.addEventListener('resize', update, { passive: true });
      window.requestAnimationFrame(update);
    });
  }

  function initAll(root = document) {
    initReveal(root);
    initAnnouncement(root);
    initStickyHeader(root);
    initSidebar(root);
    initSearch(root);
    initFilters(root);
    initSort(root);
    initGridView(root);
    initBrandSearch(root);
    initSliders(root);
    initGallery(root);
    initQuantity(root);
    initAjaxForms(root);
    initSupport(root);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll());
  else initAll();
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
